import { yupResolver } from '@hookform/resolvers/yup';
import { FormSchema } from '@precert/validation';
import axios from 'axios';
import React, { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { QueryClient } from 'react-query';
import { Container, Row, Col, Spinner, Alert } from 'reactstrap';
import urlJoin from 'url-join';
import * as Yup from 'yup';

import { PageLoader } from '../../../_components';
import { Button } from '../../../_components/button';
import { useConfig } from '../../../_components/config';
import { DateField } from '../../../_components/date-field';
import InputField from '../../../_components/input-field';
import { useToastr } from '../../../_components/toastr';
import { useCheckSession } from '../../../_components/use-check-session';
import type { AttachmentFormData } from '../create/steps/step-clinical-documents';

import styles from './styles.module.scss';
import { UpdateForm } from './update-form';

interface IdentifyParams {
  precertificationId: string;
  veteranReferralId: string;
  dateOfService: Date;
}

export interface IdentifiedData {
  formId: string;
  precertificationId: string;
  dateOfService: Date;
  attachments: AttachmentFormData[];
}

const getSchema = () => {
  const baseSchema = FormSchema().pick(['veteranReferralId', 'dateOfService']);

  return Yup.object().shape({
    precertificationId: Yup.string().required(
      'Precertification Id is required'
    ),
    veteranReferralId: baseSchema.fields.veteranReferralId,
    dateOfService: baseSchema.fields.dateOfService,
  });
};

export function ValidationForm() {
  const { loading } = useCheckSession();
  const [isSubmitting, setSubmitting] = useState(false);
  const [data, setData] = useState({
    submittedAt: new Date(),
    referralDate: '',
    referralExpirationDate: '',
    procedureCode: '',
    procedureCodesList: [],
  });
  const [submitted, setSubmitted] = useState(false);
  const [validation, setValidation] = useState(false);
  const toastr = useToastr();
  const { api, features } = useConfig();

  const schema = getSchema();

  const form = useForm({
    mode: 'all',
    resolver: yupResolver(schema),
    context: { features },
  });
  const { handleSubmit, watch, formState } = form;
  const keys = Object.keys(schema.fields);

  // TODO CCRA-37242
  const precertificationId = watch('precertificationId');
  const isDisabled = Object.keys(formState?.errors || {}).some((f) =>
    keys.includes(f)
  );
  const identifyPrecertification = async (args: IdentifyParams) => {
    setSubmitting(true);
    try {
      const queryClient = new QueryClient();
      const { data: result } = await queryClient.fetchQuery({
        queryKey: ['precertificationUpdate', args.precertificationId],
        queryFn: async () =>
          axios({
            url: urlJoin(api, '/identify'),
            method: 'post',
            withCredentials: true,
            data: {
              ...args,
            },
          }),
      });
      setSubmitting(false);
      if (result?.error) {
        toastr.error(<span>{result?.error}</span>, {
          details: result?.error,
        });
        return null;
      }
      setData(result);
      setValidation(true);
    } catch {
      setSubmitting(false);
    }
  };
  if (loading) return <PageLoader />;
  return (
    <Container
      className={`d-flex flex-column ${styles.container}`}
      fluid
      style={{ maxWidth: '850px', minHeight: '400px' }}
    >
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(identifyPrecertification)} noValidate>
          <h1
            tabIndex={0}
            className="fw-bold h4 mb-3 mt-3 text-start"
            id="main-content"
          >
            Update Precertification
            <Button
              className={styles.homeButton}
              color="primary"
              size="sm"
              href="/home"
            >
              Return Home
            </Button>
          </h1>
          <Alert color="info">
            Please enter the following information to retrieve your previously
            submitted precertification notification. You will then be able to
            upload additional medical documentation and/or update the date of
            the service, if applicable.
          </Alert>
          <Row>
            <Col lg={6}>
              <InputField
                disabled={validation}
                name="precertificationId"
                label="Precertification Id"
                required
              />
            </Col>
            <Col lg={6}>
              <InputField
                disabled={validation}
                name="veteranReferralId"
                label="Veteran Referral Id"
                required
              />
            </Col>
          </Row>

          <Row>
            <Col lg={6}>
              <DateField
                disabled={validation}
                name="dateOfService"
                label="Date Of Service"
                required
              />
            </Col>
          </Row>

          <Button
            color="success"
            disabled={validation || isSubmitting || isDisabled}
            className="mb-3"
            aria-description="Press Enter to try to identify this precertification"
            type="submit"
            id="identify-e5b4d7cb-f8be-43a5-b273-a6736c3dcb8f"
          >
            Identify
            {isSubmitting && <Spinner size="sm" className="ms-2" />}
          </Button>
        </form>
      </FormProvider>
      {validation && (
        <UpdateForm
          precertificationId={precertificationId}
          referralDate={data.referralDate}
          referralExpirationDate={data.referralExpirationDate}
          procedureCode={data.procedureCode}
          procedureCodesList={data.procedureCodesList}
          setSubmitted={setSubmitted}
          submitted={submitted}
        />
      )}
    </Container>
  );
}
